import { createContext, type ReactNode, useContext, useMemo } from 'react'
import {
  type DateInput,
  formatDate as formatDateFn,
  formatDateTime as formatDateTimeFn,
  formatRelative as formatRelativeFn,
  formatTime as formatTimeFn,
  type RelativeOptions,
  SYSTEM_DEFAULT_LOCALE,
  SYSTEM_DEFAULT_TIMEZONE,
} from '../lib/datetime'

/**
 * Holds the resolved localization context for a React subtree: the active
 * `{ locale, timezone }` plus formatters already bound to them.
 *
 * The pure functions in `../lib/datetime.ts` remain the source of truth;
 * this provider is a thin binding so components can call
 * `formatDate(value)` without threading locale/timezone through every prop.
 */
interface LocalizationContextValue {
  /** Active BCP-47 locale tag. */
  locale: string
  /** Active IANA timezone id. */
  timezone: string
  /** {@link formatDateFn} bound to the context locale/timezone. */
  formatDate: (value: DateInput) => string
  /** {@link formatDateTimeFn} bound to the context locale/timezone. */
  formatDateTime: (value: DateInput) => string
  /** {@link formatTimeFn} bound to the context locale/timezone. */
  formatTime: (value: DateInput) => string
  /** {@link formatRelativeFn} bound to the context locale/timezone. */
  formatRelative: (value: DateInput, now?: number) => string
}

const LocalizationContext = createContext<LocalizationContextValue | null>(null)

export interface LocalizationProviderProps {
  children: ReactNode
  /** Active locale. Defaults to {@link SYSTEM_DEFAULT_LOCALE} (`en-GB`). */
  locale?: string
  /** Active timezone. Defaults to {@link SYSTEM_DEFAULT_TIMEZONE} (`UTC`). */
  timezone?: string
}

/**
 * Provide a resolved `{ locale, timezone }` to a React subtree. Wrap the app
 * shell once; per-tenant / per-user preference resolution happens above this
 * (pass the resolved values in as props). Defaults to the system defaults so
 * an un-configured tree still renders sensible dates.
 */
export function LocalizationProvider({
  children,
  locale = SYSTEM_DEFAULT_LOCALE,
  timezone = SYSTEM_DEFAULT_TIMEZONE,
}: LocalizationProviderProps) {
  const value = useMemo<LocalizationContextValue>(() => {
    const opts = { locale, timezone }
    return {
      locale,
      timezone,
      formatDate: (v) => formatDateFn(v, opts),
      formatDateTime: (v) => formatDateTimeFn(v, opts),
      formatTime: (v) => formatTimeFn(v, opts),
      formatRelative: (v, now) => {
        const relOpts: RelativeOptions = { ...opts, now }
        return formatRelativeFn(v, relOpts)
      },
    }
  }, [locale, timezone])

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>
}

/** The formatter bundle returned by {@link useDateFormat}. */
export interface UseDateFormatResult {
  locale: string
  timezone: string
  formatDate: (value: DateInput) => string
  formatDateTime: (value: DateInput) => string
  formatTime: (value: DateInput) => string
  formatRelative: (value: DateInput, now?: number) => string
}

/**
 * Access the context-bound date/time formatters. Falls back to the system
 * defaults when used OUTSIDE a `<LocalizationProvider>` — this is a
 * deliberate choice: a missing provider should never crash a render, it
 * should degrade to `en-GB` / `UTC`. Wrap your app for correct results.
 */
export function useDateFormat(): UseDateFormatResult {
  const ctx = useContext(LocalizationContext)
  if (ctx) return ctx
  const opts = { locale: SYSTEM_DEFAULT_LOCALE, timezone: SYSTEM_DEFAULT_TIMEZONE }
  return {
    locale: SYSTEM_DEFAULT_LOCALE,
    timezone: SYSTEM_DEFAULT_TIMEZONE,
    formatDate: (v) => formatDateFn(v, opts),
    formatDateTime: (v) => formatDateTimeFn(v, opts),
    formatTime: (v) => formatTimeFn(v, opts),
    formatRelative: (v, now) => formatRelativeFn(v, { ...opts, now }),
  }
}
